import { useMemo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useBusinessProfile } from "@/hooks/useBusinessProfile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LoadingIndicator from "@/components/LoadingIndicator";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import {
  DECIMAL_PRECISION_OPTIONS,
  DEFAULT_DECIMAL_PRECISION,
  MAX_DECIMAL_PRECISION,
  MIN_DECIMAL_PRECISION,
  resolvePrecision,
} from "@/utils/decimal";

import { FilePond } from "react-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { FilePondFile } from "filepond";
import "@/App.css";
import { Camera, CheckCircle, CircleX, X, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  tin: /^[a-zA-Z0-9]{9,12}$/,
  contactNumber: /^[0-9+\-\s()]*$/,
  msicCode: /^\d{4,5}$/,
};

const errorMessages = {
  required: "This field is required",
  email: "Please enter a valid email address",
  tin: "TIN must be 9-12 digits",
  minLength: (length: number) => `Must be at least ${length} characters`,
  maxLength: (length: number) => `Must not exceed ${length} characters`,
  contactNumber:
    "Contact number can only contain digits, +, -, spaces, and parentheses",
};

const addressSchema = z.object({
  addressId: z.string().nullable(),
  addressLine0: z
    .string()
    .min(1, errorMessages.required)
    .max(100, errorMessages.maxLength(100)),
  addressLine1: z
    .string()
    .max(100, errorMessages.maxLength(100))
    .nullable()
    .optional(),
  addressLine2: z
    .string()
    .max(100, errorMessages.maxLength(100))
    .nullable()
    .optional(),
  postalZone: z.string().max(10, errorMessages.maxLength(10)).nullable(),
  cityName: z
    .string()
    .min(1, errorMessages.required)
    .max(50, errorMessages.maxLength(50)),
  state: z.string(),
  country: z.string(),
});

const credentialsSchema = z
  .object({
    clientName: z.string().max(120, errorMessages.maxLength(120)).optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    clientSecret2: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const fields: (keyof typeof val)[] = [
      "clientName",
      "clientId",
      "clientSecret",
      "clientSecret2",
    ];

    const trimmed = (v?: string) => (v ?? "").trim();

    const anyFilled = fields.some((f) => trimmed(val[f]) !== "");
    if (!anyFilled) {
      // all empty → OK
      return;
    }

    // if any filled, all must be non-empty
    fields.forEach((f) => {
      if (trimmed(val[f]) === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [f],
          message:
            "This field is required when any credential is provided, either empty all credential fields or fill them all",
        });
      }
    });
  });

const aiAccountingSchema = z
  .object({
    aiAccountingEmail: z
      .string()
      .transform((v) => (v.trim() === "" ? undefined : v))
      .optional()
      .refine(
        (val) => !val || validationPatterns.email.test(val),
        errorMessages.email,
      ),

    aiAccountingPassword: z
      .string()
      .transform((v) => (v.trim() === "" ? undefined : v))
      .optional(),
  })
  .superRefine((val, ctx) => {
    const email = val.aiAccountingEmail;
    const password = val.aiAccountingPassword;

    const bothEmpty = !email && !password;
    const bothFilled = email && password;

    if (bothEmpty || bothFilled) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["aiAccountingEmail"],
      message: "Email and password must both be filled or both be empty",
    });

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["aiAccountingPassword"],
      message: "Email and password must both be filled or both be empty",
    });
  });

const companyInfoSchema = z.object({
  autoSendEmailOnValid: z.boolean().default(false),
  syncBuyerMasterOnImport: z.boolean().default(false),
  trustClientDocumentTotals: z.boolean().default(false),
  decimalPrecision: z
    .number()
    .int()
    .min(MIN_DECIMAL_PRECISION, `Must be at least ${MIN_DECIMAL_PRECISION}`)
    .max(MAX_DECIMAL_PRECISION, `Must not exceed ${MAX_DECIMAL_PRECISION}`)
    .default(DEFAULT_DECIMAL_PRECISION),
  invoiceEmailSubject: z
    .string()
    .max(200, errorMessages.maxLength(200))
    .optional(),
  invoiceEmailTemplate: z
    .string()
    .max(5000, errorMessages.maxLength(5000))
    .optional(),
  name: z
    .string()
    .min(1, errorMessages.required)
    .max(300, errorMessages.maxLength(300)),
  tin: z
    .string()
    .min(1, errorMessages.required)
    .max(12, errorMessages.maxLength(12))
    .regex(validationPatterns.tin, errorMessages.tin),
  registrationNumber: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(20, errorMessages.maxLength(20)),
  tourismTaxRegistrationNumber: z
    .string()
    .max(17, { message: "Exceed max no. of characters" })
    .nullable(),
  sstRegistrationNumber: z
    .string()
    .max(17, errorMessages.maxLength(17))
    .nullable()
    .optional(),
  email: z
    .string()
    .email(errorMessages.email)
    .max(320, errorMessages.maxLength(320)),
  msicCode: z
    .string()
    .regex(validationPatterns.msicCode, "MSIC Code must be 4 or 5 digits")
    .optional(),
  businessActivityDescription: z
    .string()
    .max(300, errorMessages.maxLength(300))
    .optional(),
  baseCurrency: z.string().min(3, "Please select a valid currency"),
  contactNumber: z
    .string()
    .min(8, { message: "Contact number must be at least 8 characters" })
    .max(20, { message: "Contact number must not exceed 20 characters" })
    .regex(/^\+?\d+$/, {
      message:
        "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces",
    }),
  address: addressSchema,
  credentials: credentialsSchema,
  reportFooter: z
    .string()
    .max(1000, "Report footer must not exceed 1000 characters"),
  aiAccounting: aiAccountingSchema,
});

const DEFAULT_INVOICE_EMAIL_SUBJECT = "Invoice {{codeNumber}} - Attached";
const DEFAULT_INVOICE_EMAIL_TEMPLATE =
  "Please find attached Invoice {{codeNumber}}.";
const INVOICE_EMAIL_PLACEHOLDERS = [
  "codeNumber",
  "buyerName",
  "supplierName",
  "invoiceDate",
  "totalPayableAmount",
];

const INVOICE_EMAIL_PREVIEW_SAMPLE: Record<string, string> = {
  codeNumber: "INV-2026-0001",
  buyerName: "Sample Buyer Sdn Bhd",
  supplierName: "Your Company Sdn Bhd",
  invoiceDate: "27/07/2026",
  totalPayableAmount: "RM 1,060.00",
};

const renderInvoiceEmailPreview = (template: string): string =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => {
    return INVOICE_EMAIL_PREVIEW_SAMPLE[key] ?? match;
  });

const supportedCurrencies = [
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "GBP", name: "British Pound" },
];

type CompanyInfo = z.infer<typeof companyInfoSchema>;

type SavedProfileType = {
  businessId?: string;
  businessName?: string;
  tin?: string;
  registrationNumber?: string;
  sstRegistrationNumber?: string | null;
  tourismTaxRegistrationNumber?: string | null;
  email?: string;
  contactNumber?: string;
  msicCode?: string;
  businessActivityDescription?: string;
  addressId?: string | null;
  addressLine0?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  cityName?: string;
  state?: string;
  country?: string;
  postalZone?: string;
  baseCurrency?: string;
  companyLogo?: string | null;
  reportFooter?: string | null;

  clientId?: string | null;
  clientName?: string | null;
  clientSecret?: string | null;
  clientSecret2?: string | null;

  aiAccountingEmail?: string | null;
  aiAccountingPassword?: string | null;
};

const countries = [
  { code: "MYS", name: "Malaysia" },
  { code: "SGP", name: "Singapore" },
  { code: "IDN", name: "Indonesia" },
  { code: "THA", name: "Thailand" },
  { code: "PHL", name: "Philippines" },
  { code: "VNM", name: "Vietnam" },
  { code: "BRN", name: "Brunei" },
  { code: "KHM", name: "Cambodia" },
  { code: "LAO", name: "Laos" },
  { code: "MMR", name: "Myanmar" },
  { code: "USA", name: "United States" },
  { code: "GBR", name: "United Kingdom" },
  { code: "AUS", name: "Australia" },
  { code: "CHN", name: "China" },
  { code: "JPN", name: "Japan" },
  { code: "KOR", name: "South Korea" },
  { code: "IND", name: "India" },
  { code: "DEU", name: "Germany" },
  { code: "FRA", name: "France" },
  { code: "CAN", name: "Canada" },
];

const StateCodes = [
  { name: "Johor", code: "01" },
  { name: "Kedah", code: "02" },
  { name: "Kelantan", code: "03" },
  { name: "Melaka", code: "04" },
  { name: "Negeri Sembilan", code: "05" },
  { name: "Pahang", code: "06" },
  { name: "Pulau Pinang", code: "07" },
  { name: "Perak", code: "08" },
  { name: "Perlis", code: "09" },
  { name: "Selangor", code: "10" },
  { name: "Terengganu", code: "11" },
  { name: "Sabah", code: "12" },
  { name: "Sarawak", code: "13" },
  { name: "Wilayah Persekutuan Kuala Lumpur", code: "14" },
  { name: "Wilayah Persekutuan Labuan", code: "15" },
  { name: "Wilayah Persekutuan Putrajaya", code: "16" },
  { name: "Not Applicable", code: "17" },
];

const CompanyProfile = () => {
  const { user, isLoadingUserInfo } = useAuth();
  const {
    settings: companySettings,
    updateSettings,
    isUpdating: isUpdatingSettings,
  } = useCompanySettings();
  const [isEditing, setIsEditing] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: "",
    description: "",
    variant: "default",
  });
  const [savedProfile, setSavedProfile] = useState<SavedProfileType | null>(
    null,
  );
  const {
    updateBusinessProfile,
    isUpdatingBusinessProfile,
    uploadCompanyLogo,
  } = useBusinessProfile();
  const [isUploadingCompanyLogo, setIsUploadingCompanyLogo] =
    useState<boolean>(false);
  const [isUploadingCompanyLogoLoading, setIsUploadingCompanyLogoLoading] =
    useState<boolean>(false);
  const [files, setFiles] = useState<FilePondFile[]>([]);
  const [isRemoveCompanyConfirmation, setIsRemoveCompanyConfirmation] =
    useState<boolean>(false);
  const [reveal, setReveal] = useState({
    client1: false,
    client2: false,
    ai: false,
  });

  const queryClient = useQueryClient();

  // Prepare baseline from savedProfile/user; credentials pulled if available on user
  const companyProfile = useMemo(() => {
    const sourceData = savedProfile || user;
    return {
      autoSendEmailOnValid: companySettings?.autoSendEmailOnValid ?? false,
      syncBuyerMasterOnImport:
        companySettings?.syncBuyerMasterOnImport ?? false,
      trustClientDocumentTotals:
        companySettings?.trustClientDocumentTotals ?? false,
      decimalPrecision: resolvePrecision(companySettings?.decimalPrecision),
      invoiceEmailSubject: companySettings?.invoiceEmailSubject ?? "",
      invoiceEmailTemplate: companySettings?.invoiceEmailTemplate ?? "",
      name: sourceData?.businessName || "",
      tin: sourceData?.tin || "",
      registrationNumber: sourceData?.registrationNumber || "",
      sstRegistrationNumber: sourceData?.sstRegistrationNumber || null,
      tourismTaxRegistrationNumber:
        sourceData?.tourismTaxRegistrationNumber || null,
      email: sourceData?.email || "",
      contactNumber: sourceData?.contactNumber || "",
      msicCode: sourceData?.msicCode || "",
      businessActivityDescription:
        sourceData?.businessActivityDescription || "",
      baseCurrency: sourceData?.baseCurrency || "",
      address: {
        addressId: sourceData?.addressId || null,
        addressLine0: sourceData?.addressLine0 || "",
        addressLine1: sourceData?.addressLine1 || null,
        addressLine2: sourceData?.addressLine2 || null,
        postalZone: sourceData?.postalZone || null,
        cityName: sourceData?.cityName || "",
        state: sourceData?.state || "",
        country: sourceData?.country || "",
      },
      companyLogo: sourceData?.companyLogo || null,
      reportFooter: sourceData?.reportFooter || "",
      credentials: {
        clientId: sourceData?.clientId || "",
        clientName: sourceData?.clientName || "",
        clientSecret: sourceData?.clientSecret || "",
        clientSecret2: sourceData?.clientSecret2 || "",
      },
      aiAccounting: {
        aiAccountingEmail: sourceData?.aiAccountingEmail || "",
        aiAccountingPassword: sourceData?.aiAccountingPassword || "",
      },
    };
  }, [user, savedProfile, companySettings]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CompanyInfo>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: companyProfile,
  });

  useEffect(() => {
    if (companyProfile) {
      reset(companyProfile);
    }
  }, [companyProfile, reset]);

  const formValues = watch();

  useEffect(() => {
    if (!companyProfile) return;

    const shallowEq = (a: any, b: any): boolean => {
      // primitives or null
      if (
        a === null ||
        b === null ||
        typeof a !== "object" ||
        typeof b !== "object"
      ) {
        return a !== b;
      }

      // arrays
      if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b)) return true;
        if (a.length !== b.length) return true;
        for (let i = 0; i < a.length; i++) {
          if (shallowEq(a[i], b[i])) return true;
        }
        return false;
      }

      // objects
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const k of keys) {
        if (k === "credentials") {
          const aa = a.credentials ?? {};
          const bb = b.credentials ?? {};

          const diffClientId = aa.clientId !== bb.clientId;
          const diffClientName =
            (aa.clientName ?? "") !== (bb.clientName ?? "");

          // Trim current and original secrets
          const aSec1 = (aa.clientSecret ?? "").trim();
          const aSec2 = (aa.clientSecret2 ?? "").trim();
          const bSec1 = (bb.clientSecret ?? "").trim();
          const bSec2 = (bb.clientSecret2 ?? "").trim();

          // Secrets count as changed if they differ — including being cleared
          const secretChanged = aSec1 !== bSec1 || aSec2 !== bSec2;

          // Enable save if any field differs
          if (diffClientId || diffClientName || secretChanged) return true;

          continue;
        }

        if (k === "aiAccounting") {
          const aa = a.aiAccounting ?? {};
          const bb = b.aiAccounting ?? {};

          if ((aa.aiAccountingEmail ?? "") !== (bb.aiAccountingEmail ?? ""))
            return true;
          if (
            (aa.aiAccountingPassword ?? "") !== (bb.aiAccountingPassword ?? "")
          )
            return true;

          continue;
        }

        const av = (a as any)[k];
        const bv = (b as any)[k];

        // recurse for nested objects; otherwise compare primitive
        if (
          av !== null &&
          typeof av === "object" &&
          bv !== null &&
          typeof bv === "object"
        ) {
          if (shallowEq(av, bv)) return true;
        } else if (av !== bv) {
          return true;
        }
      }
      return false;
    };

    const hasChanges = shallowEq(formValues, companyProfile);
    setIsChanged(hasChanges);
  }, [formValues, companyProfile]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    if (companyProfile) {
      reset(companyProfile);
    }
    setIsEditing(false);
    setIsChanged(false);
  };

  const handleCurrencyChange = (value: string) => {
    setValue("baseCurrency", value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const closeAlert = () => {
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCountryChange = (value: string) => {
    setValue("address.country", value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleStateChange = (value: string) => {
    setValue("address.state", value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleUploadCompanyLogo = () => {
    setIsUploadingCompanyLogoLoading(true);
    if (files.length != 1) {
      toast({
        variant: "destructive",
        title: "Failed to upload company logo",
        description: `Please upload 1 image`,
        icon: <CircleX size={30} className="text-red-400" />,
        duration: 5000,
      });
      setIsUploadingCompanyLogoLoading(false);
      return;
    }

    const file = files[0].file as File;

    if (file) {
      uploadCompanyLogo(file, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user"] });
          setIsUploadingCompanyLogo(false);
          setIsUploadingCompanyLogoLoading(false);
          setFiles([]);
          toast({
            title: "Upload Company Logo",
            description: `Successfully upload company logo`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });
        },
        onError: (error: any) => {
          setIsUploadingCompanyLogoLoading(false);
          toast({
            variant: "destructive",
            title: "Failed to upload company logo",
            description:
              error?.response?.data?.message ||
              error?.message ||
              "Upload failed",
            icon: <CircleX size={30} className="text-red-400" />,
            duration: 5000,
          });
        },
      });
    }
  };

  const handleRemoveCompanyLogo = () => {
    const businessId = user?.businessId;
    if (!businessId) {
      setAlertConfig({
        isOpen: true,
        title: "Error",
        description: "Business ID is required to update the profile.",
        variant: "destructive",
      });
      return;
    }
    updateBusinessProfile(
      { businessId, profileData: { companyLogo: null } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user"] });
          toast({
            title: "Remove Company Logo",
            description: `Successfully remove company logo`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });
        },
        onError: (error: any) => {
          toast({
            variant: "destructive",
            title: "Failed to remove company logo",
            description:
              error?.response?.data?.message ||
              error?.message ||
              "Failed to remove company logo. Please try again.",
            icon: <CircleX size={30} className="text-red-400" />,
            duration: 5000,
          });
        },
      },
    );
  };

  const businessId = user?.businessId;

  const onSubmit = async (data: CompanyInfo) => {
    if (!businessId) {
      setAlertConfig({
        isOpen: true,
        title: "Error",
        description: "Business ID is required to update the profile.",
        variant: "destructive",
      });
      return;
    }

    try {
      // 1) update business profile
      await new Promise<void>((resolve, reject) => {
        updateBusinessProfile(
          {
            businessId,
            profileData: {
              name: data.name,
              tin: data.tin,
              registrationNumber: data.registrationNumber,
              sstRegistrationNumber: data.sstRegistrationNumber ?? null,
              tourismTaxRegistrationNumber:
                data.tourismTaxRegistrationNumber ?? null,
              email: data.email,
              contactNumber: data.contactNumber,
              msicCode: data.msicCode,
              businessActivityDescription: data.businessActivityDescription,
              baseCurrency: data.baseCurrency,
              address: data.address,
              reportFooter: data.reportFooter,
              credentials: data.credentials,
              aiAccounting: data.aiAccounting,
            },
          },
          {
            onSuccess: () => resolve(),
            onError: (err: any) => reject(err),
          },
        );
      });

      // 2) update company settings
      await updateSettings({
        autoSendEmailOnValid: data.autoSendEmailOnValid,
        syncBuyerMasterOnImport: data.syncBuyerMasterOnImport,
        trustClientDocumentTotals: data.trustClientDocumentTotals,
        decimalPrecision: data.decimalPrecision,
        invoiceEmailSubject: data.invoiceEmailSubject?.trim() || null,
        invoiceEmailTemplate: data.invoiceEmailTemplate?.trim() || null,
      });

      // update local snapshot so "isChanged" recalculates cleanly
      const updatedProfile: SavedProfileType = {
        businessId,
        businessName: data.name,
        tin: data.tin,
        registrationNumber: data.registrationNumber,
        sstRegistrationNumber: data.sstRegistrationNumber ?? null,
        tourismTaxRegistrationNumber: data.tourismTaxRegistrationNumber ?? null,
        email: data.email,
        contactNumber: data.contactNumber,
        msicCode: data.msicCode,
        businessActivityDescription: data.businessActivityDescription,
        baseCurrency: data.baseCurrency,
        addressLine0: data.address.addressLine0,
        addressLine1: data.address.addressLine1 ?? undefined,
        cityName: data.address.cityName,
        state: data.address.state,
        country: data.address.country,
        reportFooter: data.reportFooter,
        clientId: data.credentials.clientId ?? null,
        clientSecret: data.credentials.clientSecret ?? null,
        clientSecret2: data.credentials.clientSecret2 ?? null,
        clientName: data.credentials.clientName ?? null,
        aiAccountingEmail: data.aiAccounting?.aiAccountingEmail ?? null,
        aiAccountingPassword: data.aiAccounting?.aiAccountingPassword ?? null,
      };
      setSavedProfile(updatedProfile);
      queryClient.invalidateQueries({ queryKey: ["user"] });

      setAlertConfig({
        isOpen: true,
        title: "Success",
        description: "Company profile & credentials updated successfully!",
        variant: "default",
      });
      setIsEditing(false);
      setIsChanged(false);
    } catch (error: any) {
      setAlertConfig({
        isOpen: true,
        title: "Error",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update company profile/credentials. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoadingUserInfo) return <LoadingIndicator />;

  return (
    <>
      <Card className="w-full max-w-6xl mx-auto shadow p-2 pt-6">
        <CardContent>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Company Profile</h1>
          </div>
          <Separator className="my-6" />

          <div className="relative w-[200px] h-[200px] mx-auto cursor-pointer rounded-xl overflow-hidden border-2 border-gray-300 group bg-gray-100">
            <img
              src={
                companyProfile.companyLogo
                  ? `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/${companyProfile.companyLogo}?v=${Date.now()}`
                  : `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/default.jpg?v=${Date.now()}`
              }
              alt="Company Logo"
              className="w-[200px] h-[200px] object-fill"
            />

            {/* Hover Overlay */}
            <button
              onClick={() => setIsUploadingCompanyLogo(true)}
              type="button"
              className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
            >
              <div className="flex-col">
                <Camera color="white" size={45} className="mx-auto" />
                <p className="font-medium text-white text-center">Upload</p>
              </div>
            </button>
            {companyProfile.companyLogo && (
              <button
                onClick={() => setIsRemoveCompanyConfirmation(true)}
                type="button"
                className="absolute rounded-full top-1 right-1 bg-black bg-opacity-30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                title="remove"
              >
                <div className="flex-col">
                  <X size={25} className="font-medium text-white text-center" />
                </div>
              </button>
            )}

            <Dialog
              open={isUploadingCompanyLogo}
              onOpenChange={(open) => {
                if (!isUploadingCompanyLogoLoading) {
                  setIsUploadingCompanyLogo(open);
                }
              }}
            >
              <DialogContent className="h-fit max-h-[90vh] max-w-4xl flex flex-col">
                <DialogHeader>
                  <DialogTitle>Upload Company Logo</DialogTitle>
                  <DialogDescription>
                    Upload the company logo by drop your image to the dropbox
                    below.
                  </DialogDescription>
                </DialogHeader>
                <div className="w-full pt-10 pb-4">
                  <FilePond
                    onupdatefiles={(fileItems) => {
                      setFiles(fileItems);
                    }}
                    allowImagePreview={true}
                    imagePreviewMaxFileSize="5MB"
                    acceptedFileTypes={["image/*"]}
                    allowMultiple={false}
                    maxFiles={1}
                    name="files"
                    labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
                    instantUpload={false}
                    dropOnPage={true}
                    dropOnElement={false}
                    disabled={isUploadingCompanyLogoLoading}
                  />
                </div>
                <DialogFooter>
                  <div className="flex w-full gap-2">
                    <Button
                      size="default"
                      variant="default"
                      className={`w-full sm:w-1/3 mx-auto`}
                      onClick={handleUploadCompanyLogo}
                      disabled={isUploadingCompanyLogoLoading}
                    >
                      <p
                        className={`${isUploadingCompanyLogoLoading && "animate-pulse"}`}
                      >
                        {isUploadingCompanyLogoLoading
                          ? "Uploading..."
                          : "Upload Logo"}
                      </p>
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* ONE FORM: saves profile + credentials */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <h3 className="text-lg font-semibold mb-4">General Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" required>
                  Company Name
                </Label>
                <Input
                  id="name"
                  {...register("name")}
                  disabled={!isEditing}
                  placeholder="Enter company name"
                  maxLength={300}
                />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="tin" required>
                  TIN Number
                </Label>
                <Input
                  id="tin"
                  {...register("tin")}
                  disabled={!isEditing}
                  placeholder="Enter TIN number (9-12 digits)"
                  maxLength={14}
                />
                {errors.tin && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.tin.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="registrationNumber" required>
                  Registration Number
                </Label>
                <Input
                  id="registrationNumber"
                  {...register("registrationNumber")}
                  disabled={!isEditing}
                  placeholder="Enter registration number"
                  maxLength={20}
                />
                {errors.registrationNumber && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.registrationNumber.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="sstRegistrationNumber">SST Number</Label>
                <Input
                  id="sstRegistrationNumber"
                  {...register("sstRegistrationNumber")}
                  disabled={!isEditing}
                  placeholder="Enter SST number (if applicable)"
                  maxLength={17}
                />
                {errors.sstRegistrationNumber && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.sstRegistrationNumber.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="tourismTaxRegistrationNumber">
                  Tourism Tax Registration Number
                </Label>
                <Input
                  id="tourismTaxRegistrationNumber"
                  {...register("tourismTaxRegistrationNumber")}
                  disabled={!isEditing}
                  placeholder="Enter Tourism tax number (if applicable)"
                  maxLength={17}
                />
                {errors.tourismTaxRegistrationNumber && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.tourismTaxRegistrationNumber.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  disabled={!isEditing}
                  placeholder="Enter business email"
                  maxLength={320}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="contactNumber" required>
                  Contact Number
                </Label>
                <Input
                  id="contactNumber"
                  {...register("contactNumber")}
                  disabled={!isEditing}
                  placeholder="Enter contact number"
                  maxLength={15}
                />
                {errors.contactNumber && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.contactNumber.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="msicCode" required>
                  MSIC Code
                </Label>
                <Input
                  id="msicCode"
                  {...register("msicCode")}
                  disabled={!isEditing}
                  placeholder="Enter MSIC code"
                  maxLength={5}
                />
                {errors.msicCode && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.msicCode.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="baseCurrency" required>
                  Base Currency
                </Label>
                <Select
                  name="baseCurrency"
                  disabled={!isEditing}
                  value={formValues.baseCurrency || ""}
                  onValueChange={handleCurrencyChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedCurrencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.baseCurrency && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.baseCurrency.message}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="businessActivityDescription" required>
                  Business Description
                </Label>
                <Input
                  id="businessActivityDescription"
                  {...register("businessActivityDescription")}
                  disabled={!isEditing}
                  placeholder="Enter business description"
                  maxLength={300}
                />
                {errors.businessActivityDescription && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.businessActivityDescription.message}
                  </p>
                )}
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="addressLine0" required>
                  Address Line 1
                </Label>
                <Input
                  id="addressLine0"
                  {...register("address.addressLine0")}
                  disabled={!isEditing}
                  placeholder="Enter address line 1"
                  maxLength={100}
                />
                {errors.address?.addressLine0 && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.address.addressLine0.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="addressLine1">Address Line 2</Label>
                <Input
                  id="addressLine1"
                  {...register("address.addressLine1")}
                  disabled={!isEditing}
                  placeholder="Enter address line 2 (optional)"
                  maxLength={100}
                />
                {errors.address?.addressLine1 && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.address.addressLine1.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="addressLine2">Address Line 3</Label>
                <Input
                  id="addressLine2"
                  {...register("address.addressLine2")}
                  disabled={!isEditing}
                  placeholder="Enter address line 3 (optional)"
                  maxLength={100}
                />
                {errors.address?.addressLine2 && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.address.addressLine2.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="postcode">Postal Zone</Label>
                <Input
                  id="postcode"
                  {...register("address.postalZone")}
                  disabled={!isEditing}
                  placeholder="Enter postcode"
                  maxLength={100}
                />
                {errors.address?.postalZone && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.address.postalZone.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="cityName" required>
                  City
                </Label>
                <Input
                  id="cityName"
                  {...register("address.cityName")}
                  disabled={!isEditing}
                  placeholder="Enter city name"
                  maxLength={50}
                />
                {errors.address?.cityName && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.address.cityName.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="state" required>
                  State
                </Label>
                <div>
                  <Select
                    name="state"
                    disabled={!isEditing}
                    value={formValues.address?.state || ""}
                    onValueChange={handleStateChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {StateCodes.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.address?.state && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.address.state.message}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="country" required>
                  Country
                </Label>
                <div>
                  <Select
                    name="country"
                    disabled={!isEditing}
                    value={formValues.address?.country || ""}
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.address?.country && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.address.country.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">
              Line Item Decimal Precision
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="decimalPrecision" required>
                  Decimal Places on Line Items
                </Label>
                <Select
                  name="decimalPrecision"
                  disabled={!isEditing}
                  value={String(
                    formValues.decimalPrecision ?? DEFAULT_DECIMAL_PRECISION,
                  )}
                  onValueChange={(value) =>
                    setValue("decimalPrecision", Number(value), {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select decimal places" />
                  </SelectTrigger>
                  <SelectContent>
                    {DECIMAL_PRECISION_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.decimalPrecision && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.decimalPrecision.message}
                  </p>
                )}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 self-end pb-1">
                <p>
                  Controls how many decimal places are allowed on{" "}
                  <strong>line items</strong> - unit price, line discounts, fees
                  and tax. Raise this when unit prices need more than cents: at
                  2 decimals, 100,000 units &times; RM 0.1185 would be billed as
                  RM 12,000 instead of RM 11,850.
                </p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">
                  <strong>Document totals are always 2 decimal places</strong>{" "}
                  and are not affected by this setting - e-invoice submission,
                  audit reports and bank reconciliation all work in cents.
                  Rounding is applied once, when line values are added together,
                  never on the individual line.
                </p>
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">Document Totals</h3>
            <div className="flex flex-col gap-3">
              <Label>How invoice totals are decided</Label>
              <div className="inline-flex w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-1 gap-1">
                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() =>
                    setValue("trustClientDocumentTotals", false, {
                      shouldDirty: true,
                    })
                  }
                  className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed ${
                    !formValues.trustClientDocumentTotals
                      ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  Recalculate from line items
                </button>
                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() =>
                    setValue("trustClientDocumentTotals", true, {
                      shouldDirty: true,
                    })
                  }
                  className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed ${
                    formValues.trustClientDocumentTotals
                      ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  Trust totals from my system
                </button>
              </div>

              {!formValues.trustClientDocumentTotals ? (
                <div className="text-sm rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20 p-3 text-blue-800 dark:text-blue-300">
                  <p className="font-semibold mb-1">
                    Recalculate from line items{" "}
                    <span className="font-normal">(recommended)</span>
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-400">
                    <li>
                      Invoice totals are always recalculated from the line items
                      after saving
                    </li>
                    <li>
                      Tax, net and payable totals are guaranteed to match the sum
                      of the lines
                    </li>
                    <li>
                      The tax breakdown sent to MyInvois always matches the
                      line-level tax, which LHDN validates
                    </li>
                    <li>
                      The sales report shows the same figures as the invoice
                      screen
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="text-sm rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20 p-3 text-amber-800 dark:text-amber-300">
                  <p className="font-semibold mb-1">
                    Trust totals from my system
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-400">
                    <li>
                      Header totals sent by your POS/PMS through the API — or
                      entered in the amounts sheet of the import file — are
                      stored exactly as received
                    </li>
                    <li>
                      The sales report also shows these figures, so QuickBill
                      matches your own system end to end
                    </li>
                    <li>Use this when QuickBill must tally with your existing system</li>
                    <li>
                      <strong>
                        If your header totals disagree with your line items,
                        MyInvois may reject the submission
                      </strong>{" "}
                      — the tax total must equal the sum of the line-level tax
                    </li>
                    <li>
                      Consolidated invoices are built from these header figures,
                      so an incorrect total carries into the consolidated
                      e-invoice
                    </li>
                    <li>
                      Applies to invoices created <strong>after</strong> the
                      change; existing invoices are not updated
                    </li>
                  </ul>
                </div>
              )}

              <p className="text-xs text-slate-500 dark:text-slate-500">
                Invoices created directly in QuickBill are always calculated from
                their line items, whichever mode is selected. Line values keep
                their full precision either way; document totals are always
                rounded once, to 2 decimal places.
              </p>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">
              Report Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="">Report Footer</Label>
                <div>
                  <Textarea
                    {...register("reportFooter")}
                    disabled={!isEditing}
                    placeholder="Your footer in printed PDF invoice goes here"
                  />
                  {errors.reportFooter && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.reportFooter.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">
              Client Credentials
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="clientName">Client Name</Label>
                <Input
                  id="clientName"
                  {...register("credentials.clientName")}
                  disabled={!isEditing}
                  placeholder="A label to recognize these credentials"
                  maxLength={120}
                />
                {errors.credentials?.clientName && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.credentials.clientName.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="clientId">Client ID</Label>
                <Input
                  id="clientId"
                  {...register("credentials.clientId")}
                  disabled={!isEditing}
                  placeholder="Enter Client ID"
                  autoComplete="off"
                />
                {errors.credentials?.clientId && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.credentials.clientId.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="clientSecret">Client Secret</Label>
                <div className="relative">
                  <Input
                    id="clientSecret"
                    type={reveal.client1 ? "text" : "password"}
                    {...register("credentials.clientSecret")}
                    disabled={!isEditing}
                    placeholder={
                      isEditing ? "Enter Client Secret" : "••••••••••••"
                    }
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setReveal((r) => ({ ...r, client1: !r.client1 }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                    title={reveal.client1 ? "Hide" : "Show"}
                    disabled={!isEditing}
                  >
                    {reveal.client1 ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.credentials?.clientSecret && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.credentials.clientSecret.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="clientSecret2">Client Secret 2</Label>
                <div className="relative">
                  <Input
                    id="clientSecret2"
                    type={reveal.client2 ? "text" : "password"}
                    {...register("credentials.clientSecret2")}
                    disabled={!isEditing}
                    placeholder={
                      isEditing ? "Enter Client Secret 2" : "••••••••••••"
                    }
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setReveal((r) => ({ ...r, client2: !r.client2 }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                    title={reveal.client2 ? "Hide" : "Show"}
                    disabled={!isEditing}
                  >
                    {reveal.client2 ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.credentials?.clientSecret2 && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.credentials.clientSecret2.message}
                  </p>
                )}
              </div>
            </div>
            <h3 className="text-lg font-semibold mt-8 mb-4">
              AI Accounting Login
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="aiEmail">Email</Label>
                <Input
                  id="aiEmail"
                  type="email"
                  {...register("aiAccounting.aiAccountingEmail")}
                  disabled={!isEditing}
                  placeholder="Enter AI Accounting email"
                />
                {errors.aiAccounting?.aiAccountingEmail && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.aiAccounting.aiAccountingEmail.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="aiPassword">Password</Label>
                <div className="relative">
                  <Input
                    id="aiPassword"
                    type={reveal.ai ? "text" : "password"}
                    {...register("aiAccounting.aiAccountingPassword")}
                    disabled={!isEditing}
                    placeholder="Enter AI Accounting password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((r) => ({ ...r, ai: !r.ai }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                    disabled={!isEditing}
                  >
                    {reveal.ai ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {errors.aiAccounting?.aiAccountingPassword && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.aiAccounting.aiAccountingPassword.message}
                  </p>
                )}
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">Email Settings</h3>
            <div className="flex items-center gap-3">
              <Checkbox
                id="autoSendEmailOnValid"
                checked={formValues.autoSendEmailOnValid ?? false}
                onCheckedChange={(checked) =>
                  setValue("autoSendEmailOnValid", checked === true, {
                    shouldDirty: true,
                  })
                }
                disabled={!isEditing}
              />
              <Label htmlFor="autoSendEmailOnValid" className="cursor-pointer">
                Automatically send invoice email to buyer when status becomes
                Valid
              </Label>
            </div>

            <div className="grid grid-cols-1 gap-4 mt-6">
              <div>
                <Label htmlFor="invoiceEmailSubject">
                  Invoice Email Subject
                </Label>
                <Input
                  id="invoiceEmailSubject"
                  {...register("invoiceEmailSubject")}
                  disabled={!isEditing}
                  placeholder={DEFAULT_INVOICE_EMAIL_SUBJECT}
                  maxLength={200}
                />
                {errors.invoiceEmailSubject && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.invoiceEmailSubject.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="invoiceEmailTemplate">Invoice Email Body</Label>
                <Textarea
                  id="invoiceEmailTemplate"
                  {...register("invoiceEmailTemplate")}
                  disabled={!isEditing}
                  placeholder={DEFAULT_INVOICE_EMAIL_TEMPLATE}
                  rows={6}
                  maxLength={5000}
                />
                {errors.invoiceEmailTemplate && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.invoiceEmailTemplate.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Leave both fields blank to use the default subject/body shown
                  as placeholder text above. Available placeholders —{" "}
                  {INVOICE_EMAIL_PLACEHOLDERS.map((p) => `{{${p}}}`).join(", ")}
                  . This template is used both when clicking "Send Invoice" and
                  for automatic emails sent when an invoice becomes Valid.
                </p>
              </div>

              {isEditing &&
                (formValues.invoiceEmailSubject ||
                  formValues.invoiceEmailTemplate) && (
                  <div className="rounded-md border bg-muted/30 p-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Preview (sample data)
                    </p>
                    <p className="text-sm font-semibold">
                      {renderInvoiceEmailPreview(
                        formValues.invoiceEmailSubject ||
                          DEFAULT_INVOICE_EMAIL_SUBJECT,
                      )}
                    </p>
                    <p className="text-sm whitespace-pre-wrap mt-2">
                      {renderInvoiceEmailPreview(
                        formValues.invoiceEmailTemplate ||
                          DEFAULT_INVOICE_EMAIL_TEMPLATE,
                      )}
                    </p>
                  </div>
                )}
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4">Import Settings</h3>
            <div className="flex items-center gap-3">
              <Checkbox
                id="syncBuyerMasterOnImport"
                checked={formValues.syncBuyerMasterOnImport ?? false}
                onCheckedChange={(checked) =>
                  setValue("syncBuyerMasterOnImport", checked === true, {
                    shouldDirty: true,
                  })
                }
                disabled={!isEditing}
              />
              <Label
                htmlFor="syncBuyerMasterOnImport"
                className="cursor-pointer"
              >
                Sync buyer master on import — when enabled, importing will
                create new buyers in the buyer master and overwrite buyer fields
                on imported invoices with data from the buyer master
              </Label>
            </div>

            <div className="flex justify-end pt-6 gap-2">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={
                      !isChanged ||
                      isUpdatingBusinessProfile ||
                      isUpdatingSettings
                    }
                  >
                    {isUpdatingBusinessProfile || isUpdatingSettings
                      ? "Saving..."
                      : "Save"}
                  </Button>
                </>
              ) : (
                <Button type="button" onClick={handleEdit} variant="default">
                  Edit
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <AlertDialog open={alertConfig.isOpen} onOpenChange={closeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle
              className={
                alertConfig.variant === "destructive"
                  ? "text-red-600"
                  : "text-green-600"
              }
            >
              {alertConfig.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {alertConfig.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={closeAlert}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isRemoveCompanyConfirmation}
        onOpenChange={() => setIsRemoveCompanyConfirmation(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Company Logo</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove company logo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleRemoveCompanyLogo}
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CompanyProfile;
